import { CompanyConstant, companyRequiredFieldList } from "presentation/constant/Company/CompanyConstant";
import { CustomerTypeDroOpts } from "presentation/constant/DropDownOptions/Company/CustomerTypeDroOpts";
import { useCompanyMaintenanceVM } from "presentation/hook/Company/useCompanyMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";

import { useCompanyMaintenanceTracked } from "presentation/store/Company/CompanyMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FieldType, HPHButton, HPHCheckbox, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const CompanyFormPanel = () => {
    const [companyState] = useCompanyMaintenanceTracked();
    const companyMaintenanceVM = useCompanyMaintenanceVM();
    const messageBarVM = useMessageBarVM();
    const COMPANY_CONST = CompanyConstant.Header;
    const currentCompany = companyState.currentCompany;
    const [isLoading, setIsLoading] = useState(false);
    const { dynamicOptions, isShowMaster, isShowAlliance, isMasterCompany, isAlliance, searchStatus } = companyState
    const { isRead, isAdd, isEditable, allFormState, isSaveClicked } = companyState.viewState
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;

    useEffect(() => {
        if (companyState.isShowRightPanel || (companyState.selectCompanyRows && companyState.selectCompanyRows.length > 0)) {
            companyMaintenanceVM.onInitDefaultValue();
        }
    }, [companyMaintenanceVM, companyState.isShowRightPanel, companyState.selectCompanyRows]);

    const handleCancelClick = useCallback(() => {
        companyMaintenanceVM.onCancel();
    }, [companyMaintenanceVM])

    const handleSaveClick = useCallback(async () => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        let valid = false;
        if (currentCompany.emailAddress) {
            const emails = currentCompany.emailAddress
                .split(";")
                .map(email => email.trim())
                .filter(email => email !== "");
            currentCompany.emailAddress = emails.join(";");
            emails?.forEach(email => {
                if (!emailRegex.test(email)) {
                    messageBarVM.showError("Email address is invalid.");
                    valid = true;
                    return;
                }
            });
        }
        if (valid) {
            return;
        }
        setIsLoading(true);
        await companyMaintenanceVM.onSave(currentCompany, isAdd).then(async (data) => {
            if (data && data["saveCompanyFailed"]) {
                setIsLoading(false);
                messageBarVM.showError(data["saveCompanyFailed"]?.toString());
                return;
            }
            if (!data || data["saveCompanyFailed"] !== "") {
                if (searchStatus) {
                    await companyMaintenanceVM.onGroupButtonClick(searchStatus).then(() => {
                        setIsLoading(false);
                    }).catch((error) => {
                        setIsLoading(false);
                    });
                } else {
                    await companyMaintenanceVM.onSearch(companyState.searchCriteria, true).then(() => {
                        setIsLoading(false);
                    }).catch((error) => {
                        setIsLoading(false);
                    });
                }
            } else {
                setIsLoading(false);
            }
        }).catch(() => {
            setIsLoading(false);
            messageBarVM.showError('Save Data Failed.');
        });
    }, [companyMaintenanceVM, companyState.searchCriteria, currentCompany, isAdd, messageBarVM, searchStatus])

    const memoCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["companyCode"] : ""}
                isReadOnly={!isAdd || isEditable || isRead}
                isShowOptional={false}
                readOnlyValue={currentCompany?.companyCode ?? ''}
                fieldValue={currentCompany?.companyCode ?? ''}
                fieldLabel={currentCompany.companyType === 'ALLIANCE' ? COMPANY_CONST.ALLIANCE_CODE : COMPANY_CONST.COMPANY_CODE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'companyCode'}
                maxLength={10}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COMPANY_CONST.ALLIANCE_CODE, COMPANY_CONST.COMPANY_CODE, allFormState, companyMaintenanceVM, currentCompany?.companyCode, currentCompany.companyType, isAdd, isEditable, isRead, isSaveClicked])

    const memoCompanyName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.companyName ?? ''}
                fieldValue={currentCompany?.companyName ?? ''}
                fieldLabel={COMPANY_CONST.COMPANY_NAME}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'companyName'}
                maxLength={120}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COMPANY_CONST.COMPANY_NAME, allFormState, companyMaintenanceVM, currentCompany?.companyName, isRead])

    const memoCompanyType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["companyType"] : ""}
                isReadOnly={!(isAdd || isEditable) || isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                //disableInput={false}
                isSaveClicked={isSaveClicked}
                readOnlyValue={currentCompany?.companyType ?? ''}
                fieldValue={currentCompany?.companyType ?? ''}
                fieldLabel={COMPANY_CONST.COMPANY_TYPE}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'companyType'}
                options={dynamicOptions.companyTypeDropdownOptions}
                maxLength={60}
                requiredFieldList={companyRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue)} />
        </div>
        , [allFormState, isAdd, isEditable, isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, isSaveClicked, currentCompany?.companyType, COMPANY_CONST.COMPANY_TYPE, dynamicOptions.companyTypeDropdownOptions, companyMaintenanceVM])

    const memoMasterCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) || isRead}
                isShowOptional={true}
                isMultipleDropDown={false}
                //disableInput={false}
                readOnlyValue={currentCompany?.masterCompanyCode ?? ''}
                fieldValue={currentCompany?.masterCompanyCode ?? ''}
                fieldLabel={COMPANY_CONST.MASTER_COMPANY}
                //isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'masterCompanyCode'}
                isLimitOptions={true}
                options={dynamicOptions.masterCompanyDropdownOptions}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue: any) =>
                    companyMaintenanceVM.onDropdownKeyChange(fieldKey, fieldValue, fFullValue, "masterCompanyId")} />
        </div>
        , [allFormState, isShowMaster, isShowAlliance, isAlliance, isMasterCompany, isRead, currentCompany?.masterCompanyCode, COMPANY_CONST.MASTER_COMPANY, dynamicOptions.masterCompanyDropdownOptions, companyMaintenanceVM])

    const memoAllianceCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) || isRead}
                isShowOptional={true}
                isMultipleDropDown={false}
                //disableInput={false}
                readOnlyValue={currentCompany?.allianceCompanyCode ?? ''}
                fieldValue={currentCompany?.allianceCompanyCode ?? ''}
                fieldLabel={COMPANY_CONST.ALLIANCE}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'allianceCompanyCode'}
                isLimitOptions={true}
                options={dynamicOptions.allianceCompanyDropdownOptions}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue: any) =>
                    companyMaintenanceVM.onDropdownKeyChange(fieldKey, fieldValue, fFullValue, "allianceCompanyId")} />
        </div>
        , [allFormState, isShowMaster, isShowAlliance, isAlliance, isMasterCompany, isRead, currentCompany?.allianceCompanyCode, COMPANY_CONST.ALLIANCE, dynamicOptions.allianceCompanyDropdownOptions, companyMaintenanceVM])

    const memoAddress1 = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "430px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.address1 ?? ''}
                fieldValue={currentCompany?.address1 ?? ''}
                fieldLabel={COMPANY_CONST.ADDRESS_1}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'address1'}
                maxLength={150}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isShowAlliance, isAlliance, isRead, COMPANY_CONST.ADDRESS_1, currentCompany?.address1, allFormState, companyMaintenanceVM])

    const memoAddress2 = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "430px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.address2 ?? ''}
                fieldValue={currentCompany?.address2 ?? ''}
                fieldLabel={COMPANY_CONST.ADDRESS_2}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'address2'}
                maxLength={150}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isShowAlliance, isAlliance, isRead, currentCompany?.address2, COMPANY_CONST.ADDRESS_2, companyMaintenanceVM])

    const memoAddress3 = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "430px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.address3 ?? ''}
                fieldValue={currentCompany?.address3 ?? ''}
                fieldLabel={COMPANY_CONST.ADDRESS_3}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'address3'}
                maxLength={150}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isShowAlliance, isAlliance, isRead, currentCompany?.address3, COMPANY_CONST.ADDRESS_3, companyMaintenanceVM])

    const memoCountryCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["countryCode"] : ""}
                isReadOnly={!(isAdd || isEditable) || isShowAlliance || isAlliance || isRead}
                isShowOptional={false}
                //disableInput={false}
                isSaveClicked={isSaveClicked}
                readOnlyValue={currentCompany?.countryCode ?? ''}
                fieldValue={currentCompany?.countryCode ?? ''}
                fieldLabel={COMPANY_CONST.COUNTRY_CODE}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'countryCode'}
                options={dynamicOptions.countryCodeDropdownOptions}
                maxLength={60}
                requiredFieldList={companyRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue: any) =>
                    companyMaintenanceVM.onDropdownKeyChange(fieldKey, fieldValue, fFullValue, "countryCodeId")} />
        </div>
        , [allFormState, isAdd, isEditable, isShowAlliance, isAlliance, isRead, isSaveClicked, currentCompany?.countryCode, COMPANY_CONST.COUNTRY_CODE, dynamicOptions.countryCodeDropdownOptions, companyMaintenanceVM])

    const memoSignOnDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={isShowAlliance || isAlliance || isRead}
                label={COMPANY_CONST.SIGN_ON_DATE}
                width="200px"
                date={currentCompany?.signOnDate ?? null}
                fieldName="signOnDate"
                errorMessage={allFormState ? allFormState["signOnDate"] : ""}
                onDateChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue)/*(e) => onFieldChange(e, 'signOnDate')*/} />
        </div>
        , [isShowAlliance, isAlliance, isRead, COMPANY_CONST.SIGN_ON_DATE, currentCompany?.signOnDate, allFormState, companyMaintenanceVM])

    const memoCutomerType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["customerType"] : ""}
                isReadOnly={!isAdd || isEditable || isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={false}
                //disableInput={false}
                isSaveClicked={isSaveClicked}
                readOnlyValue={currentCompany?.customerType ?? ''}
                fieldValue={currentCompany?.customerType ?? ''}
                fieldLabel={COMPANY_CONST.CUSTOMER_TYPE}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'customerType'}
                options={currentCompany.companyType === 'LINE' ? CustomerTypeDroOpts().getCustomerTypeModel() : CustomerTypeDroOpts().getCCCustomerTypeModel()}
                maxLength={60}
                requiredFieldList={companyRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue)} />
        </div>
        , [allFormState, isAdd, isEditable, isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, isSaveClicked, currentCompany?.customerType, currentCompany.companyType, COMPANY_CONST.CUSTOMER_TYPE, companyMaintenanceVM])

    const memoActiveInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            {/*<NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!(isAdd || isEditable) || isRead}
                isShowOptional={false}
                isMultipleDropDown={false}
                //disableInput={false}
                isSaveClicked={isSaveClicked}
                readOnlyValue={currentCompany?.activeInd ?? ''}
                fieldValue={currentCompany?.activeInd ?? ''}
                fieldLabel={COMPANY_CONST.ACTIVE_IND}
                //isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'activeInd'}
                options={ActiveIndDroOpts().getDroOptsModel()}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue)} />*/}
            <NbisCommonField
                fieldKey={"activeInd"}
                requiredFieldList={[]}
                fieldLabel={COMPANY_CONST.ACTIVE_IND}
                fieldType={FieldType.TEXT}
                isReadOnly={true}
                onFieldChange={() => { }} />
            <HPHCheckbox
                label={""}
                checked={currentCompany?.activeInd === 'Y' ? true : false}
                disabled={!(isAdd || isEditable) || isRead}
                onChange={(e) => companyMaintenanceVM.onCheckboxChange(e.checked, "activeInd")}
            />
        </div>
        , [COMPANY_CONST.ACTIVE_IND, companyMaintenanceVM, currentCompany?.activeInd, isAdd, isEditable, isRead])

    const memoMobilePhoneNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["mobileNo"] : ""}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={false}
                readOnlyValue={currentCompany?.mobileNo ?? ''}
                fieldValue={currentCompany?.mobileNo ?? ''}
                fieldLabel={COMPANY_CONST.MOBILE_PHONE_NO}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'mobileNo'}
                maxLength={15}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, currentCompany?.mobileNo, COMPANY_CONST.MOBILE_PHONE_NO, isSaveClicked, companyMaintenanceVM])

    const memoContactPerson = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>

            <NbisCommonField
                errorMessage={allFormState ? allFormState["contactPerson"] : ""}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={false}
                readOnlyValue={currentCompany?.contactPerson ?? ''}
                fieldValue={currentCompany?.contactPerson ?? ''}
                fieldLabel={COMPANY_CONST.CONTACT_PERSON}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'contactPerson'}
                maxLength={50}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, currentCompany?.contactPerson, COMPANY_CONST.CONTACT_PERSON, isSaveClicked, companyMaintenanceVM])

    const memoEmailAddress = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "430px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["emailAddress"] : ""}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={false}
                readOnlyValue={currentCompany?.emailAddress ?? ''}
                fieldValue={currentCompany?.emailAddress ?? ''}
                fieldLabel={COMPANY_CONST.EMAIL_ADDRESS}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.TEXT}
                fieldKey={'emailAddress'}
                maxLength={300}
                isTextFieldCapitalize={false}
                requiredFieldList={companyRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, currentCompany?.emailAddress, COMPANY_CONST.EMAIL_ADDRESS, isSaveClicked, companyMaintenanceVM])

    const memoGstNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.gstNo ?? ''}
                fieldValue={currentCompany?.gstNo ?? ''}
                fieldLabel={COMPANY_CONST.GST_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'gstNo'}
                maxLength={20}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, COMPANY_CONST.GST_NO, currentCompany?.gstNo, allFormState, companyMaintenanceVM])

    const memoNtnNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.ntnNo ?? ''}
                fieldValue={currentCompany?.ntnNo ?? ''}
                fieldLabel={COMPANY_CONST.NTN_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'ntnNo'}
                maxLength={30}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, COMPANY_CONST.NTN_NO, currentCompany?.ntnNo, allFormState, companyMaintenanceVM])

    const memoLocalAgentCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.localAgentCode ?? ''}
                fieldValue={currentCompany?.localAgentCode ?? ''}
                fieldLabel={COMPANY_CONST.LOCAL_AGENT_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'localAgentCode'}
                maxLength={4}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, COMPANY_CONST.LOCAL_AGENT_CODE, currentCompany?.localAgentCode, allFormState, companyMaintenanceVM])

    const memoLocalAgentName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.localAgentName ?? ''}
                fieldValue={currentCompany?.localAgentName ?? ''}
                fieldLabel={COMPANY_CONST.LOCAL_AGENT_NAME}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'localAgentName'}
                maxLength={4}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, COMPANY_CONST.LOCAL_AGENT_NAME, currentCompany?.localAgentName, allFormState, companyMaintenanceVM])

    const memoLocalAgentNtn = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.localAgentNtn ?? ''}
                fieldValue={currentCompany?.localAgentNtn ?? ''}
                fieldLabel={COMPANY_CONST.LOCAL_AGENT_NTN}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'localAgentNtn'}
                maxLength={4}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, COMPANY_CONST.LOCAL_AGENT_NTN, currentCompany?.localAgentNtn, allFormState, companyMaintenanceVM])

    const memoLocalAgentStrn = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.localAgentStrn ?? ''}
                fieldValue={currentCompany?.localAgentStrn ?? ''}
                fieldLabel={COMPANY_CONST.LOCAL_AGENT_STRN}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'localAgentStrn'}
                maxLength={4}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, currentCompany?.localAgentStrn, COMPANY_CONST.LOCAL_AGENT_STRN, companyMaintenanceVM])

    const memoLocalAgentAddress1 = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "430px", marginBottom: "24px" }}>

            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.localAgentAddress1 ?? ''}
                fieldValue={currentCompany?.localAgentAddress1 ?? ''}
                fieldLabel={COMPANY_CONST.LOCAL_AGENT_ADDESS1}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'localAgentAddress1'}
                maxLength={50}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, currentCompany?.localAgentAddress1, COMPANY_CONST.LOCAL_AGENT_ADDESS1, companyMaintenanceVM])

    const memoLocalAgentAddress2 = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "430px", marginBottom: "24px" }}>

            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.localAgentAddress2 ?? ''}
                fieldValue={currentCompany?.localAgentAddress2 ?? ''}
                fieldLabel={COMPANY_CONST.LOCAL_AGENT_ADDESS2}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'localAgentAddress2'}
                maxLength={50}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, currentCompany?.localAgentAddress2, COMPANY_CONST.LOCAL_AGENT_ADDESS2, companyMaintenanceVM])

    const memoLocalAgentAddress3 = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "430px", marginBottom: "24px" }}>

            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={isShowMaster || isMasterCompany || isShowAlliance || isAlliance || isRead}
                isShowOptional={true}
                readOnlyValue={currentCompany?.localAgentAddress3 ?? ''}
                fieldValue={currentCompany?.localAgentAddress3 ?? ''}
                fieldLabel={COMPANY_CONST.LOCAL_AGENT_ADDESS3}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'localAgentAddress3'}
                maxLength={50}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [isShowMaster, isMasterCompany, isShowAlliance, isAlliance, isRead, COMPANY_CONST.LOCAL_AGENT_ADDESS3, currentCompany?.localAgentAddress3, allFormState, companyMaintenanceVM])

    const memoAgenNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentCompany?.agentNo ?? ''}
                fieldValue={currentCompany?.agentNo ?? ''}
                fieldLabel={COMPANY_CONST.AGENT_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'agentNo'}
                maxLength={4}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COMPANY_CONST.AGENT_NO, allFormState, companyMaintenanceVM, currentCompany?.agentNo])

    const memoAgenName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentCompany?.agentName ?? ''}
                fieldValue={currentCompany?.agentName ?? ''}
                fieldLabel={COMPANY_CONST.AGENT_NAME}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'agentName'}
                maxLength={4}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COMPANY_CONST.AGENT_NAME, allFormState, companyMaintenanceVM, currentCompany?.agentName])

    const memoLetPassAccount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentCompany?.letPassAccountInd ?? ''}
                fieldValue={currentCompany?.letPassAccountInd ?? ''}
                fieldLabel={COMPANY_CONST.LET_PASS_ACCOUNT}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'letPassAccountInd'}
                maxLength={4}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COMPANY_CONST.LET_PASS_ACCOUNT, allFormState, companyMaintenanceVM, currentCompany?.letPassAccountInd])

    const memoLetPassAccountBalance = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "200px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={currentCompany?.lapBalance ?? ''}
                fieldValue={currentCompany?.lapBalance ?? ''}
                fieldLabel={COMPANY_CONST.LET_PASS_ACCOUNT_BALANCE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'lapBalance'}
                maxLength={4}
                isTextFieldCapitalize={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyMaintenanceVM.onFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COMPANY_CONST.LET_PASS_ACCOUNT_BALANCE, allFormState, companyMaintenanceVM, currentCompany?.lapBalance])

    return (
        <div className='side-form-content-wrapper'>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{'Company Information'}</SidebarTitle>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ height: '75vh', maxHeight: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoCompanyType}
                            {memoCompanyName}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoMasterCompanyCode}
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoAllianceCompanyCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCompanyCode}
                            {!(isShowAlliance || isAlliance) && memoCountryCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {!(isShowAlliance || isAlliance) && memoAddress1}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {!(isShowAlliance || isAlliance) && memoAddress2}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {!(isShowAlliance || isAlliance) && memoAddress3}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {!(isShowAlliance || isAlliance) && memoSignOnDate}
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoCutomerType}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoMobilePhoneNo}
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoContactPerson}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoEmailAddress}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoGstNo}
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoNtnNo}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoAgenNo}
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoAgenName}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLetPassAccount}
                            {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLetPassAccountBalance}
                        </CriteriaItemContainer>
                        {(operatingUnit === 'SAPT' || operatingUnit === 'KICT') && <>
                            <CriteriaItemContainer>
                                {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLocalAgentCode}
                                {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLocalAgentNtn}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLocalAgentName}
                                {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLocalAgentStrn}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLocalAgentAddress1}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLocalAgentAddress2}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {!(isShowMaster || isMasterCompany || isShowAlliance || isAlliance) && memoLocalAgentAddress3}
                            </CriteriaItemContainer>
                        </>}
                        <CriteriaItemContainer>
                            {memoActiveInd}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>

            <div className='im-charge-data-search-criteria-buttons'>
                {
                    <>
                        <HPHButton label={isAdd || isEditable ? 'Cancel' : 'Close'} size={'Small'} theme={'Secondary'} onClick={() => handleCancelClick()} />
                        {(isAdd || isEditable) && <HPHButton label={isAdd ? 'Add' : isEditable ? 'Update' : 'Save'} size={'Small'} theme={'Primary'} onClick={() => handleSaveClick()} />}
                    </>
                }
            </div>

        </div>
    )
}
export default memo(CompanyFormPanel);